import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@asset-metrix/portal-ui';
import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';

import { NO_ROLE_ID } from '../../api/portalGatewayClient';
import { useStore } from '../../store';
import { pageWithRoleLink } from '../../utils/urlPath';

import styles from './infoIconModal.module.scss';

export type InfoIconModalProps = {
  onClose: () => void;
  opened: boolean;
  headline?: string;
  text?: string;
  withinPortal?: boolean;
};

export function InfoIconModal({
  opened,
  onClose,
  headline,
  text,
  withinPortal,
}: InfoIconModalProps) {
  const roleId = useStore((state) => state.activeUserRole?.id);
  const navigate = useNavigate();
  const content = useMemo(() => {
    try {
      return text && stateToHTML(convertFromRaw(JSON.parse(text)));
    } catch (e: unknown) {
      console.error(e);
      return undefined;
    }
  }, [text]);

  const captureLinkClicks = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    let el = e.target;
    while (
      el &&
      el instanceof Element &&
      el.nodeName !== 'A' &&
      el.parentNode != null
    ) {
      el = el.parentNode;
    }

    if (!(el instanceof HTMLAnchorElement)) {
      return;
    }

    const href = el.href;
    const loc = globalThis.location;
    const baseUrl = `${loc.protocol}//${loc.host}`;
    const isInternalLink = href.slice(0, baseUrl.length) === baseUrl;

    if (isInternalLink) {
      const path = href.slice(baseUrl.length);
      navigate(
        pageWithRoleLink(roleId ?? NO_ROLE_ID, { pageType: 'page', slug: path })
      );
    } else {
      window.open(href, '_blank');
    }
  };

  return (
    <Modal
      modalType="info"
      opened={opened}
      onClose={onClose}
      size={768}
      title={headline}
      withinPortal={withinPortal}
    >
      <div
        data-testid="info-box-modal-content"
        className={styles['content']}
        onClick={captureLinkClicks}
      >
        <div
          data-testid="info-box-modal-content-text"
          className={styles['content--text']}
          dangerouslySetInnerHTML={
            content != null ? { __html: content } : undefined
          }
        ></div>
      </div>
    </Modal>
  );
}
